import { memo, useMemo } from "react";

import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useTypedContext from "hooks/useTypedContext";
import { SCHEDULING_TYPE } from "shared/Stack/Scheduling/types";
import { StackContext } from "views/Stack/Context";

import { StackSchedulingContextApi, StackSchedulingContextData } from "../Context";
import PermissionBasedDropdown, {
  PermissionBasedDropdownItem,
} from "./PermissionBasedDropdown";

type StackSchedulingCreateDropdownProps = {
  componentLocation?: "Header" | "Empty state";
};

const StackSchedulingCreateDropdown = ({
  componentLocation,
}: StackSchedulingCreateDropdownProps) => {
  const { canManageStackAndRuns, canCreateDriftDetection } = useTypedContext(StackContext);

  const trackSegmentEvent = useAnalytics({
    page: AnalyticsPageStack.StackScheduling,
  });

  const { driftDetectionExist } = useTypedContext(StackSchedulingContextData);
  const { onCreate } = useTypedContext(StackSchedulingContextApi);

  const items: PermissionBasedDropdownItem[] = useMemo(() => {
    const handleCreating = (type: SCHEDULING_TYPE) => () => {
      onCreate(type);
      trackSegmentEvent("Create Schedule Clicked", { type, buttonLocation: componentLocation });
    };

    return [
      {
        id: "drift-detection",
        label: "Drift detection",
        onAction: handleCreating(SCHEDULING_TYPE.DRIFT_DETECTION),
        canPerformAction: canCreateDriftDetection,
        hidden: driftDetectionExist,
      },
      {
        id: "delete-stack",
        label: "Stack deletion",
        onAction: handleCreating(SCHEDULING_TYPE.DELETE_STACK),
        canPerformAction: canManageStackAndRuns,
      },
      {
        id: "task",
        label: "Task",
        onAction: handleCreating(SCHEDULING_TYPE.TASK),
        canPerformAction: canManageStackAndRuns,
      },
      {
        id: "run",
        label: "Run",
        onAction: handleCreating(SCHEDULING_TYPE.RUN),
        canPerformAction: canManageStackAndRuns,
      },
    ];
  }, [
    canCreateDriftDetection,
    canManageStackAndRuns,
    driftDetectionExist,
    onCreate,
    trackSegmentEvent,
    componentLocation,
  ]);

  return <PermissionBasedDropdown buttonLabel="Create schedule" items={items} />;
};

export default memo(StackSchedulingCreateDropdown);
